import { Warehouse, TrendingUp, TrendingDown, ArrowRight, Users } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

interface ResourcePool {
  amount: number;
  icon?: string;
  description?: string;
  tags?: string[];
}

interface ManpowerBatch {
  role: string;
  count: number;
  experience?: string;
  icon?: string;
  tags?: string[];
  linked_npc_ids?: string[];
  status?: string;
}

interface ResourcePanelProps {
  resourcePools?: Record<string, ResourcePool> | null;
  resourceProduction?: Record<string, { type: string; amount: number; location_name?: string }> | null;
  resourceUpkeep?: Record<string, number> | null;
  resourceNet?: Record<string, number> | null;
  manpowerRoster?: Record<string, ManpowerBatch> | null;
  // Economy cross-reference
  currencyAmount?: number | null;
  currencyName?: string | null;
  orgIncome?: number | null;
  // Legacy
  resources?: Record<string, number> | null;
  resourceTypes?: string[] | null;
}

export function ResourcePanel({ resourcePools, resourceProduction, resourceUpkeep, resourceNet, manpowerRoster, currencyAmount, currencyName, orgIncome, resources, resourceTypes }: ResourcePanelProps) {
  const prod = resourceProduction || {};
  const upkeep = resourceUpkeep || {};
  const net = resourceNet || {};
  const roster = manpowerRoster || {};

  // Normalize resource entries
  const entries: { name: string; amount: number; icon: string; description: string; tags: string[] }[] = [];

  if (resourcePools && Object.keys(resourcePools).length > 0) {
    for (const [name, pool] of Object.entries(resourcePools)) {
      const p = pool as any;
      entries.push({
        name,
        amount: typeof p?.amount === "number" ? p.amount : 0,
        icon: p?.icon || "📦",
        description: p?.description || "",
        tags: Array.isArray(p?.tags) ? p.tags : [],
      });
    }
  } else if (resources) {
    const types = resourceTypes || Object.keys(resources);
    for (const t of types) {
      entries.push({ name: t, amount: resources[t] ?? 0, icon: LEGACY_ICONS[t] || "📦", description: "", tags: [] });
    }
  }

  const rosterEntries = Object.entries(roster).filter(([, b]) => b && typeof b === "object");
  const hasResources = entries.length > 0;
  const hasPersonnel = rosterEntries.length > 0;

  if (!hasResources && !hasPersonnel && Object.keys(prod).length === 0) {
    return (
      <div className="text-xs text-muted-foreground/50 text-center py-4">
        No resources tracked yet. Resources will appear as the story introduces them.
      </div>
    );
  }

  const maxResource = Math.max(1, ...entries.map(e => e.amount));

  return (
    <div {...storyElement("resource-panel")} className="space-y-4">
      {/* Currency / Treasury */}
      {currencyAmount != null && currencyAmount > 0 && (
        <div className="flex items-center justify-between px-2 py-1.5 rounded bg-muted/30 border border-border/30">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            💰 {currencyName || "Gold"}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-foreground">{currencyAmount}</span>
            {orgIncome != null && orgIncome > 0 && (
              <span className="text-[10px] text-green-400 flex items-center gap-0.5">
                <TrendingUp className="w-2.5 h-2.5" />+{orgIncome}/turn
              </span>
            )}
          </div>
        </div>
      )}
      {/* Stockpile */}
      {hasResources && (
        <div className="space-y-3">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
            <Warehouse className="w-3 h-3" /> Stockpile
          </div>
          {entries.map((entry) => {
            const netVal = net[entry.name] || 0;
            const pct = (entry.amount / maxResource) * 100;

            return (
              <div key={entry.name} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium capitalize flex items-center gap-1 min-w-0 truncate" title={entry.description || undefined}>
                    {entry.icon} {entry.name}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold text-foreground">{entry.amount}</span>
                    {netVal !== 0 && (
                      <span className={`text-[10px] flex items-center gap-0.5 ${netVal > 0 ? "text-green-400" : "text-destructive"}`}>
                        {netVal > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                        {netVal > 0 ? "+" : ""}{netVal}/turn
                      </span>
                    )}
                  </div>
                </div>
                <Progress value={pct} className="h-1.5" />
                {entry.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {entry.tags.map(tag => (
                      <span key={tag} className="text-[9px] text-muted-foreground/60 bg-muted/30 px-1 rounded">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Personnel / Manpower */}
      {hasPersonnel && (
        <>
          {hasResources && <Separator />}
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Personnel
            </div>
            {rosterEntries.map(([id, batch]) => {
              const b = batch as ManpowerBatch;
              const linkedCount = b.linked_npc_ids?.length || 0;
              return (
                <div key={id} className="flex items-center justify-between text-xs gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span>{b.icon || "👤"}</span>
                    <span className="font-medium truncate">{b.role || id}</span>
                    {b.experience && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">
                        {b.experience}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="font-bold text-foreground">{b.count ?? 0}</span>
                    {linkedCount > 0 && (
                      <span className="text-[9px] text-muted-foreground" title={`${linkedCount} linked NPC(s)`}>
                        ({linkedCount} named)
                      </span>
                    )}
                    {b.status && b.status !== "active" && (
                      <Badge variant="secondary" className="text-[9px] px-1 py-0">
                        {b.status}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Production Sources */}
      {Object.keys(prod).length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" /> Production
            </div>
            {(Object.entries(prod) as [string, any][]).map(([locId, p]) => (
              <div key={locId} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="truncate">{p.location_name || locId}</span>
                <ArrowRight className="w-3 h-3 shrink-0" />
                <Badge variant="outline" className="text-[10px] px-1.5">
                  +{p.amount} {p.type}
                </Badge>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Upkeep */}
      {Object.keys(upkeep).some((k) => (upkeep[k] || 0) > 0) && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <TrendingDown className="w-3 h-3" /> Upkeep
            </div>
            {Object.entries(upkeep)
              .filter(([, v]) => (v || 0) > 0)
              .map(([resType, cost]) => (
                <div key={resType} className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="capitalize">{resType}</span>
                  <span className="text-destructive">-{cost}/turn</span>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

const LEGACY_ICONS: Record<string, string> = {
  troops: "⚔️",
  workers: "👷",
  materials: "🧱",
  intel: "🔍",
  influence: "👑",
};
